'use client';

import { Suspense, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { EventWizard } from '@/components/event-wizard/EventWizard';
import { ORGANIZATION_DASHBOARD_PATH } from '@/lib/role-dashboard';
import { readActiveWorkspace, writeEventOrigin } from '@/lib/workspace-context';
import { readRememberedWorkspaceEventId } from '@/lib/resolve-static-param';

function NewEventContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [organizationId, setOrganizationId] = useState<string | undefined>();
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const step = searchParams?.get('step');
    const rememberedId = readRememberedWorkspaceEventId();

    // If reloading while on an advanced step (e.g. registration, tickets) with an existing draft,
    // seamlessly resume that draft rather than starting over with an empty wizard.
    if (step && step !== 'basics' && rememberedId) {
      setRedirecting(true);
      router.replace(`/dashboard/events/${rememberedId}/edit?step=${step}`);
      return;
    }

    const workspace = searchParams?.get('workspace');
    const orgIdParam = searchParams?.get('organization_id');

    if (workspace === 'organization') {
      writeEventOrigin({
        href: ORGANIZATION_DASHBOARD_PATH,
        label: 'Organization Dashboard',
      });
    }

    const activeWorkspace = readActiveWorkspace();
    const resolved =
      orgIdParam ??
      (workspace === 'organization' ? activeWorkspace?.organizationId ?? undefined : activeWorkspace?.organizationId ?? undefined);
    setOrganizationId(resolved || undefined);
  }, [searchParams, router]);

  if (redirecting) {
    return <div className="p-8 text-center text-sm text-nature-muted">Restoring your event draft...</div>;
  }

  return <EventWizard createMode organizationId={organizationId} />;
}

export default function NewEventPage() {
  return (
    <Suspense fallback={<div className="p-8 text-center text-sm text-nature-muted">Loading wizard...</div>}>
      <NewEventContent />
    </Suspense>
  );
}
